import { Button } from "@/components/ui/button";
import {
  useBuyMoreMailboxes,
  useGetPaymentMethods,
} from "@/hooks/use-subscription";
import {
  b2bPlanObject,
  B2BPlanType,
  b2cPlanObject,
  B2CPlanType,
  PlanCategory,
} from "@/pages/billing/pricing-data";
import { IPurchasePlan, PaymentInterval } from "@/types/subscription.types";
import { CreditCard, DollarSign, Loader2, ShoppingCart } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { CounterInput } from "@/components/counter-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { PaymentIntervalWithoutCustom2 } from "@/stores/downgrade-store";

const BuyMoreMailboxesContent = ({
  currentPlan,
  subscriptionId,
  quantity = 1,
  interval,
  onClose,
}: {
  currentPlan: IPurchasePlan;
  subscriptionId: string;
  quantity?: number;
  interval: PaymentIntervalWithoutCustom2;
  onClose: () => void;
}) => {
  const [additionalMailboxes, setAdditionalMailboxes] = useState(quantity);

  const [showConfirmation, setShowConfirmation] = useState(false);

  const { data: paymentMethods } = useGetPaymentMethods();

  // Type guards for plan type
  const isB2C = currentPlan.planId?.category === PlanCategory.B2C;
  const isB2B = currentPlan.planId?.category === PlanCategory.B2B;
  const isCustom = currentPlan.isCustomPlan;

  // Get the correct plan object safely, (data that is used in the plan card)
  const currentSubscriptionPlan = useMemo(() => {
    if (
      isB2C &&
      currentPlan.planId &&
      Object.prototype.hasOwnProperty.call(
        b2cPlanObject,
        currentPlan.planId.planType
      )
    ) {
      return b2cPlanObject[currentPlan.planId.planType as B2CPlanType];
    }
    if (
      isB2B &&
      currentPlan.planId &&
      Object.prototype.hasOwnProperty.call(
        b2bPlanObject,
        currentPlan.planId.planType
      )
    ) {
      return b2bPlanObject[currentPlan.planId.planType as B2BPlanType];
    }

    if (isCustom && currentPlan.addonPlanId) {
      return currentPlan.addonPlanId;
    }

    return undefined;
  }, [currentPlan, isB2C, isB2B, isCustom]);

  // Parse price safely, fallback to 0 if not found or not a number
  const perMailboxPrice = useMemo(() => {
    if (!currentSubscriptionPlan) return 0;

    if (isCustom && "price" in currentSubscriptionPlan) {
      return currentSubscriptionPlan.price as number;
    }

    // Handle regular B2B/B2C plans
    if ("earlyBirdPrice" in currentSubscriptionPlan) {
      const price =
        currentSubscriptionPlan.earlyBirdPrice[interval] ?? "0";
      const parsed = Number(price);
      return isNaN(parsed) ? 0 : parsed;
    }

    return 0;
  }, [currentSubscriptionPlan, isCustom, interval]);

  const currentSubscriptionPrice = perMailboxPrice * currentPlan.quantity;

  const additionalCost = additionalMailboxes * perMailboxPrice;
  const newTotal = currentSubscriptionPrice + additionalCost;

  const { mutateAsync: buyMoreMailboxes, isPending: isBuyingMoreMailboxes } =
    useBuyMoreMailboxes();

  const handleConfirmBuy = async () => {
    try {
      const planId = currentPlan.isCustomPlan
        ? currentPlan.addonPlanId?._id
        : currentPlan.planId?._id;

      if (!planId) {
        toast.error("No plan ID found");
        return;
      }

      await buyMoreMailboxes({
        subscriptionId,
        planId,
        quantity: additionalMailboxes,
      });

      window.cioanalytics?.track("more_mailboxes_purchased", {
        plan: currentPlan.planId?.planType,
        quantity: additionalMailboxes,
      });

      onClose();
      toast.success("Mailboxes purchased successfully");
    } catch (error) {
      toast.error("Failed to purchase mailboxes");
    }
  };

  const handleShowConfirmation = () => {
    // check if default payment method is set
    if (!paymentMethods?.defaultPaymentMethodId) {
      toast.error("Please set a default payment method first.");
      return;
    }

    setShowConfirmation(true);
  };

  const renderPlanName = () => {
    if (currentPlan.planId) {
      return `${currentPlan.planId.category} ${currentPlan.planId.planType}`;
    }

    if (currentPlan.isCustomPlan) {
      return `Custom ${currentPlan.addonPlanId?.maxEmailsPerDay}`;
    }

    return "Unknown";
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Buy More Mailboxes</DialogTitle>
        <DialogDescription>
          Add more mailboxes to your current plan.
        </DialogDescription>
      </DialogHeader>

      {/* Current Plan Info */}
      <div className="bg-muted/50 p-4 rounded-md mb-4">
        <h3 className="font-medium mb-2">Current Plan: {renderPlanName()}</h3>
        <div className="text-sm text-muted-foreground grid gap-1">
          <div className="flex justify-between">
            <span>Mailboxes:</span>
            <span className="font-mono tabular-nums">
              {currentPlan.quantity}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Price per mailbox:</span>
            <span className="font-mono tabular-nums">
              ${perMailboxPrice.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Base subscription:</span>
            <span className="font-mono tabular-nums">
              ${currentSubscriptionPrice.toFixed(2)}/mo
            </span>
          </div>
        </div>
      </div>

      {/* Mailbox Addition - Now side by side */}
      <div className="space-y-2">
        <Label htmlFor="mailboxes">Additional mailboxes and cost</Label>
        <div className="grid grid-cols-2 items-center gap-4">
          <CounterInput
            count={additionalMailboxes}
            setCount={setAdditionalMailboxes}
            min={1}
            disabled={isBuyingMoreMailboxes}
          />
          <div className="flex items-center justify-end">
            <DollarSign className="h-4 w-4 mr-1" />
            <span className="font-mono tabular-nums">
              {additionalCost.toFixed(2)}/mo
            </span>
          </div>
        </div>

        <div className="text-xs text-muted-foreground mt-1">
          Currently, you will only be charged for the additional mailboxes.
        </div>
      </div>

      {/* New Total Summary */}
      <div className="mt-2">
        <div className="border-t border-dashed border-border my-4"></div>
        <div className="flex justify-between items-center">
          <span className="text-sm">Old total:</span>
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mr-1" />
            <span className="font-mono tabular-nums">
              {currentSubscriptionPrice.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm">Additional cost:</span>
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mr-1" />
            <span className="font-mono tabular-nums">
              {additionalCost.toFixed(2)}
            </span>
          </div>
        </div>

        <hr className="border-dashed border-border my-2" />
        <div className="flex justify-between items-center">
          <span className="font-medium">New total:</span>
          <div className="flex items-center font-bold">
            <DollarSign className="h-4 w-4 mr-1" />
            <span className="font-mono tabular-nums">
              {newTotal.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onClose()}>
          Cancel
        </Button>

        <Button
          variant="default"
          className="gap-2"
          onClick={handleShowConfirmation}
          disabled={isBuyingMoreMailboxes}
        >
          {isBuyingMoreMailboxes ? (
            <>
              <Loader2 className="animate-spin" />
              Buying...
            </>
          ) : (
            <>
              <CreditCard className="h-4 w-4" />
              Buy Now
            </>
          )}
        </Button>
      </DialogFooter>

      {showConfirmation && (
        <PaymentConfirmationDialog
          showConfirmation={showConfirmation}
          setShowConfirmation={setShowConfirmation}
          handleConfirmBuy={handleConfirmBuy}
        />
      )}
    </>
  );
};

export const BuyMoreMailboxesDialog = ({
  currentPlan,
  subscriptionId,
  interval,
  quantity = 1,
  variant = "outline",
  className,
}: {
  currentPlan: IPurchasePlan;
  subscriptionId: string;
  interval: PaymentIntervalWithoutCustom2;
  quantity?: number;
  variant?: "outline" | "default";
  className?: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} className={className}>
          <ShoppingCart />
          Buy More Mailboxes
        </Button>
      </DialogTrigger>
      <DialogContent>
        {open && (
          <BuyMoreMailboxesContent
            currentPlan={currentPlan}
            subscriptionId={subscriptionId}
            interval={interval}
            quantity={quantity}
            onClose={() => setOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PaymentConfirmationDialog = ({
  showConfirmation,
  setShowConfirmation,
  handleConfirmBuy,
}: {
  showConfirmation: boolean;
  setShowConfirmation: (show: boolean) => void;
  handleConfirmBuy: () => void;
}) => {
  return (
    <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Your Purchase</AlertDialogTitle>
          <AlertDialogDescription>
            This action will complete your order and charge your default payment
            method. Do you wish to proceed?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="default" onClick={handleConfirmBuy}>
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
